import { type ReactNode } from 'react';
import type { VerificationIconKey } from '@/modules/attendance/domain/constants/verificationMode';

export interface VerificationIconProps {
  iconKey: VerificationIconKey;
  size?: number;
  color?: string;
}

// ---------------------------------------------------------------------------
// SVG inner paths per icon key
// All icons share: viewBox="0 0 24 24", fill="none", stroke, strokeWidth="2",
// strokeLinecap="round". Paths sourced from web/Clock_Web.html lines 364-373
// and 761, with improvisations for keys not present in the HTML reference.
// ---------------------------------------------------------------------------

function FingerprintPaths() {
  return (
    <>
      <path d="M12 11a3 3 0 0 1 3 3 11.97 11.97 0 0 1-2.91 7.81" />
      <path d="M9.86 5.42a7.97 7.97 0 0 1 9.31 4.84" />
      <path d="M3.13 9.43A8 8 0 0 1 5.5 6.2" />
      <path d="M4 18.69a16.04 16.04 0 0 0 4-9.69 4 4 0 0 1 8 0v.5" />
      <path d="M16 14a6 6 0 0 0-6 5.84" />
    </>
  );
}

function CardPaths() {
  return (
    <>
      <rect x="2" y="6" width="20" height="14" rx="2" />
      <path d="M16 12h.01" />
      <path d="M20 9c-1.333 1-2 1.5-2 3s.667 2 2 3" />
    </>
  );
}

function PinPaths() {
  return (
    <>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  );
}

function FacialPaths() {
  return (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </>
  );
}

function IrisPaths() {
  return (
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>
  );
}

function PlatePaths() {
  return (
    <>
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <line x1="7" y1="11" x2="7.01" y2="11" />
      <line x1="11" y1="11" x2="17" y2="11" />
      <line x1="7" y1="14" x2="17" y2="14" />
    </>
  );
}

function UserPaths() {
  return (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  );
}

function PcPaths() {
  return (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>
  );
}

function MobilePaths() {
  return (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </>
  );
}

function PalmPaths() {
  return (
    <>
      <path d="M18 11V6a2 2 0 0 0-4 0v5" />
      <path d="M14 10V4a2 2 0 0 0-4 0v6" />
      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
      <path d="M18 8a2 2 1 0 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </>
  );
}

function BiometricPaths() {
  return (
    <>
      <path d="M17.8 19.8A9 9 0 1 0 6.2 19.8" />
      <path d="M12 2v4" />
      <circle cx="12" cy="12" r="3" />
    </>
  );
}

function ClockWebPaths() {
  return (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  );
}

function ClockMobilePaths() {
  return (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <circle cx="12" cy="11" r="3" fill="currentColor" fillOpacity="0.15" />
      <path d="M12 9.5v1.5l1 1" />
    </>
  );
}

function ManualPaths() {
  return (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  );
}

function UnknownPaths() {
  return (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  );
}

const ICON_PATHS: Record<VerificationIconKey, ReactNode> = {
  fingerprint: <FingerprintPaths />,
  card: <CardPaths />,
  pin: <PinPaths />,
  facial: <FacialPaths />,
  iris: <IrisPaths />,
  plate: <PlatePaths />,
  user: <UserPaths />,
  pc: <PcPaths />,
  mobile: <MobilePaths />,
  palm: <PalmPaths />,
  biometric: <BiometricPaths />,
  // appWeb and appMobile share the PC and Mobile paths respectively
  appWeb: <PcPaths />,
  appMobile: <MobilePaths />,
  clockWeb: <ClockWebPaths />,
  clockMobile: <ClockMobilePaths />,
  manual: <ManualPaths />,
  unknown: <UnknownPaths />,
};

/**
 * Renders a small SVG icon for a verification method or source key.
 *
 * @param iconKey - One of the 17 VerificationIconKey values.
 * @param size    - SVG width & height in px. Default: 12.
 * @param color   - SVG stroke color. Default: 'currentColor'.
 */
export function VerificationIcon({ iconKey, size = 12, color = 'currentColor' }: VerificationIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {ICON_PATHS[iconKey] ?? ICON_PATHS.unknown}
    </svg>
  );
}
